import { StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

import { EXCLUSIVE_VALUES, nextSelection } from "./check-question-logic";
import type { QuestionComponentProps } from "./types";

import { colors, fonts } from "@/lib/theme";

function parseArray(value: string | undefined): string[] {
    if (!value) {
        return [];
    }
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

/**
 * Multi-select chip control — flat-section v2 visual.
 *
 * Rounded-pill chips wrap in a flex row. Selected = ink fill (or ink2 for
 * exclusive options) + white text. Unselected exclusive options get a dashed
 * border so the mutual-exclusion behavior is discoverable. The italic hint
 * below explains the dashed convention.
 *
 * Wire format unchanged: JSON-encoded array of selected option.value strings.
 */
export function CheckQuestion({ question, value, onChange, readOnly }: QuestionComponentProps) {
    const selected = parseArray(value);

    const toggle = (optionValue: string) => {
        if (readOnly) {
            return;
        }
        onChange(JSON.stringify(nextSelection(selected, optionValue)));
    };

    return (
        <View>
            <View style={styles.wrap}>
                {question.answerOptions.map((option) => {
                    const on = selected.includes(option.value);
                    const exclusive = EXCLUSIVE_VALUES.has(option.value);
                    const chipStyle = [
                        styles.chip,
                        on
                            ? exclusive
                                ? styles.chipOnExclusive
                                : styles.chipOn
                            : exclusive
                              ? styles.chipOffExclusive
                              : styles.chipOff
                    ];
                    return (
                        <TouchableOpacity
                            key={option.id}
                            activeOpacity={0.85}
                            onPress={() => toggle(option.value)}
                            disabled={readOnly}
                            style={chipStyle}
                        >
                            <Text
                                style={[
                                    styles.chipText,
                                    on ? styles.chipTextOn : styles.chipTextOff
                                ]}
                            >
                                {option.text}
                            </Text>
                        </TouchableOpacity>
                    );
                })}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    wrap: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8
    },
    chip: {
        paddingVertical: 10,
        paddingHorizontal: 16,
        borderRadius: 999,
        borderWidth: 1.5
    },
    chipOn: {
        backgroundColor: colors.ink,
        borderColor: colors.ink
    },
    chipOnExclusive: {
        backgroundColor: colors.gray900, // #1F1547 — v2 "ink2"
        borderColor: colors.gray900
    },
    chipOff: {
        backgroundColor: colors.surface,
        borderColor: colors.border,
        borderStyle: "solid"
    },
    chipOffExclusive: {
        backgroundColor: colors.surface,
        borderColor: colors.gray400,
        borderStyle: "dashed"
    },
    chipText: {
        fontFamily: fonts.bold,
        fontSize: 13
    },
    chipTextOn: { color: "#FFFFFF" },
    chipTextOff: { color: colors.ink }
});
